import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { TreeView } from '../tree-view/TreeView';
import { PrivateTreeAccessDenied } from '../tree-view/PrivateTreeAccessDenied';
import { DevControls } from './DevControls';
import { ControlBar } from '../chrome/ControlBar';
import { useTreeInteractor, useTreeState } from '../tree-domain/TreeInteractorContext';
import { TreeInteractorProvider } from '../tree-domain/TreeInteractorContext';
import { AppProviders } from '../providers/AppProviders';
import { AuthGate } from '../auth/AuthGate';
import { useIsEditLayoutNarrowViewport } from '../ui/useIsNarrowViewport';
import { useUndoRedoShortcuts } from '../tree-domain/useUndoRedoShortcuts';
import { EditStatusControls } from './EditStatusControls';
import { TreeLoadingOverlay } from '../tree-view/TreeLoadingOverlay';
import { usePersistor } from '../persistence/PersistorContext';
import { DeployInteractor, type DeployState } from './DeployInteractor';
import { downloadBytes } from './downloadBytes';

const TreeEditContent: React.FC<{ treeId: string }> = ({ treeId }) => {
  const interactor = useTreeInteractor();
  const treeState = useTreeState();
  const isNarrowViewport = useIsEditLayoutNarrowViewport();
  const [isDevPanelCollapsed, setIsDevPanelCollapsed] = useState(() => isNarrowViewport);
  const [isSaving, setIsSaving] = useState(false);

  useUndoRedoShortcuts(interactor);

  const handleUndo = useCallback(() => interactor.undo(), [interactor]);
  const handleRedo = useCallback(() => interactor.redo(), [interactor]);

  const handleSave = useCallback(async () => {
    if (isSaving) return; // Prevent multiple saves
    
    try {
      setIsSaving(true);
      await interactor.saveData();
      console.log('Tree saved successfully');
    } catch (error) {
      console.error('Failed to save tree:', error);
    } finally {
      setIsSaving(false);
    }
  }, [interactor, isSaving]);

  // --- Deploy: package this tree as a self-contained artifact and download it ---

  const persistor = usePersistor();
  const deployInteractor = useMemo(
    () => new DeployInteractor({ persistor, treeId, rendererBuildId: __BUILD_ID__ }),
    [persistor, treeId],
  );
  const [deployState, setDeployState] = useState<DeployState>(() =>
    deployInteractor.getState(),
  );

  useEffect(() => {
    deployInteractor.setUpdateCallback(setDeployState);
    return () => deployInteractor.setUpdateCallback(() => {});
  }, [deployInteractor]);

  const handleDeploy = useCallback(async () => {
    deployInteractor.dismissError();
    // The artifact is built from the published tree; unsaved edits are not in it.
    if (
      treeState.isDraft &&
      !window.confirm(
        'This tree has unpublished changes. Deploy will package the last published version. Continue?',
      )
    ) {
      return;
    }
    const result = await deployInteractor.deploy();
    if (result) {
      // Object URLs and anchors stay out of the interactor: jsdom has no
      // URL.createObjectURL, and nothing here is worth a test double.
      downloadBytes(result.zip, result.fileName, 'application/zip');
    }
  }, [deployInteractor, treeState.isDraft]);

  const handleDevPanelCollapse = () => {
    setIsDevPanelCollapsed(!isDevPanelCollapsed);
  };

  const handleAddNode = useCallback(() => {
    const data = interactor.getData();
    interactor.addNode("New Node", data.viewport, window.innerWidth, window.innerHeight);
  }, [interactor]);

  const handleAddGuide = useCallback(
    async (nodeId: string) => {
      const result = await interactor.attachGuideToLeaf(nodeId);
      if (!result.success) {
        console.error('TreeEdit: attachGuideToLeaf failed', result.error);
      }
    },
    [interactor],
  );

  const handleDeleteGuide = useCallback(
    async (nodeId: string): Promise<boolean> => {
      const result = await interactor.deleteGuideFromLeaf(nodeId);
      if (!result.success) {
        window.alert(result.error);
        return false;
      }
      return true;
    },
    [interactor],
  );

  const handleSetViewport = useCallback(() => {
    const data = interactor.getData();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Use the same logic as TreeView's getViewportCenter function
    const centerX = windowWidth / 2;
    const centerY = windowHeight / 2;
    
    // Reverse the centering formula to get the center coordinates from current viewport
    // The centering formula is: newX = -node.fx * k + centerX
    // To get the center coordinates: node.fx = (centerX - newX) / k
    const centerCoordinatesX = (centerX - data.viewport.x) / data.viewport.k;
    const centerCoordinatesY = (centerY - data.viewport.y) / data.viewport.k;
    
    // Set the center coordinates as the initial viewport
    interactor.setInitialViewport({
      x: centerCoordinatesX,
      y: centerCoordinatesY,
      k: data.viewport.k
    });
  }, [interactor]);

  // The DRAFT/undo/redo cluster, defined once for both hosts: the wide control bar's
  // editStatus slot, or the dev panel bar on narrow viewports. Building (and gating)
  // it in one place keeps the two layouts from ever disagreeing.
  const editStatus = !treeState.isLoading ? (
    <EditStatusControls
      showDraft={treeState.isDraft}
      onUndo={handleUndo}
      onRedo={handleRedo}
      canUndo={treeState.canUndo}
      canRedo={treeState.canRedo}
    />
  ) : undefined;

  return (
    <div className="edit-container">
      <ControlBar
        isDraft={treeState.isDraft}
        onSave={handleSave}
        showSaveButton={treeState.isDraft}
        isEditMode={true}
        treeId={treeId}
        isSaving={isSaving}
        isLoading={treeState.isLoading ?? false}
        canEdit={true} // If user is in edit mode, they must have edit permissions (AuthGate ensures this)
        treeName={treeState.tree.name}
        onRenameTree={(name) => interactor.updateTreeName(name)}
        editStatus={isNarrowViewport ? undefined : editStatus}
        onDeploy={() => void handleDeploy()}
        isDeploying={deployState.phase === 'working'}
      />

      {/* Publish/auto-save failure notice; the interactor clears it on the next successful save. */}
      {treeState.persistenceError && (
        <div className="persistence-error-banner" role="alert">
          {treeState.persistenceError}
        </div>
      )}

      {deployState.error && (
        <div className="persistence-error-banner" role="alert">
          {`Deploy failed: ${deployState.error}`}
        </div>
      )}

      <div className="edit-main-content">
        {!treeState.isLoading && (
          <div className={`dev-panel ${isDevPanelCollapsed ? 'collapsed' : ''}`}>
            <DevControls
              data={treeState.tree}
              onCollapse={handleDevPanelCollapse}
              isCollapsed={isDevPanelCollapsed}
              onAddNode={handleAddNode}
              onSetViewport={handleSetViewport}
              selectedNodeId={treeState.tree.selectedNodeId}
              onRenameNode={interactor.renameNode.bind(interactor)}
              onDeleteNode={interactor.deleteNode.bind(interactor)}
              onDeleteLink={interactor.deleteLink.bind(interactor)}
              onAddGuide={handleAddGuide}
              onDeleteGuide={handleDeleteGuide}
              editStatus={isNarrowViewport ? editStatus : undefined}
            />
          </div>
        )}
        
        <TreeView
          treeId={treeId}
          showDevControls={true}
          skillTreeData={treeState.tree}
          leftOffset={treeState.isLoading ? 0 : (isDevPanelCollapsed ? 60 : 300)}
          hideTitleBar={isNarrowViewport}
          privateAccessDeniedView={<PrivateTreeAccessDenied />}
        />

        {/* Rendered last so it stacks above the canvas and panels while loading. */}
        {treeState.isLoading && <TreeLoadingOverlay message="Opening tree for editing…" />}
      </div>
    </div>
  );
};

export const TreeEdit: React.FC = () => {
  const { treeId } = useParams<{ treeId: string }>();
  const treeIdValue = treeId || 'tekken-8';
  
  return (
    <AppProviders treeId={treeIdValue}>
      <AuthGate isDevelopment={true}>
        <TreeInteractorProvider key={treeIdValue} isDevMode={true} treeId={treeIdValue}>
          <TreeEditContent treeId={treeIdValue} />
        </TreeInteractorProvider>
      </AuthGate>
    </AppProviders>
  );
};